import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Headphones, Send, Loader2, User, Bot, RefreshCw, CheckCircle2 } from "lucide-react";

export function LiveSupportInbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/support/threads");
      setThreads(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadMessages = useCallback(async (t) => {
    if (!t) return;
    try {
      const { data } = await api.get(`/admin/support/messages?user_id=${t.user_id}&session_id=${t.session_id}`);
      setMessages(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadThreads(); const i = setInterval(loadThreads, 8000); return () => clearInterval(i); }, [loadThreads]);
  useEffect(() => {
    if (!active) return;
    loadMessages(active);
    const i = setInterval(() => loadMessages(active), 6000);
    return () => clearInterval(i);
  }, [active, loadMessages]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const openThread = (t) => { setActive(t); setMessages([]); };

  const send = async () => {
    const msg = reply.trim();
    if (!msg || !active || sending) return;
    setReply("");
    setSending(true);
    try {
      await api.post("/admin/support/reply", { user_id: active.user_id, session_id: active.session_id, message: msg });
      await loadMessages(active);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const closeThread = async () => {
    if (!active) return;
    try {
      await api.post("/admin/support/close", { user_id: active.user_id, session_id: active.session_id });
      toast.success("Konvèsasyon fèmen");
      setActive({ ...active, status: "closed" });
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reopenThread = async () => {
    if (!active) return;
    try {
      await api.post("/admin/support/reopen", { user_id: active.user_id, session_id: active.session_id });
      toast.success("Konvèsasyon relouvri");
      setActive({ ...active, status: "open" });
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="border border-[#222] bg-[#0F0F0F]" data-testid="live-support-inbox">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#222]">
        <div className="flex items-center gap-2">
          <Headphones className="h-4 w-4 text-[#00F593]" />
          <h3 className="font-heading font-semibold text-sm">Sipò Ajan (Chat Kliyan)</h3>
        </div>
        <button onClick={loadThreads} data-testid="support-refresh" className="text-[#5f7ba6] hover:text-white"><RefreshCw className="h-4 w-4" /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 min-h-[420px]">
        {/* Thread list */}
        <div className="border-r border-[#222] max-h-[520px] overflow-y-auto" data-testid="support-thread-list">
          {loading ? (
            <div className="p-6 text-center text-[#666] text-sm"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-center text-[#666] text-xs">Poko gen chat kliyan.</div>
          ) : threads.map((t) => (
            <button key={`${t.user_id}-${t.session_id}`} onClick={() => openThread(t)}
              data-testid={`support-thread-${t.user_id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#1a1a1a] hover:bg-[#151515] transition-colors ${active && active.user_id === t.user_id && active.session_id === t.session_id ? "bg-[#151515]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm truncate">{t.user_name || t.username || "Kliyan"}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {t.status === "closed" && <span className="text-[9px] font-mono-term uppercase tracking-widest text-[#22e07b] bg-[#22e07b]/10 px-1.5 py-0.5 rounded-full">Rezoud</span>}
                  {t.unread_admin && <span className="h-2 w-2 rounded-full bg-[#00F593]" data-testid="support-unread-dot" />}
                </div>
              </div>
              <div className="text-[11px] text-[#666] truncate">{t.user_email}</div>
              <div className="text-[11px] text-[#888] truncate mt-0.5">{t.last_message}</div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className="md:col-span-2 flex flex-col max-h-[520px]">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[#555] text-sm p-6">Chwazi yon konvèsasyon pou reponn.</div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-[#222] flex items-center justify-between gap-2">
                <span className="text-xs text-[#8fb4e6] truncate">{active.user_name} · {active.user_email}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {active.status === "closed" ? (
                    <>
                      <span className="text-[10px] font-mono-term uppercase tracking-widest text-[#22e07b] bg-[#22e07b]/10 px-2 py-0.5 rounded-full" data-testid="support-closed-badge">Rezoud</span>
                      <button onClick={reopenThread} data-testid="support-reopen-button"
                        className="text-[11px] text-[#8fb4e6] hover:text-white border border-[#222] rounded-full px-2.5 py-1">Relouvri</button>
                    </>
                  ) : (
                    <button onClick={closeThread} data-testid="support-close-button"
                      className="text-[11px] text-[#00F593] hover:text-white border border-[#00F593]/40 hover:bg-[#00F593]/10 rounded-full px-2.5 py-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Fèmen
                    </button>
                  )}
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2.5" data-testid="support-conversation">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "agent" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "agent" ? "bg-[#00F593]/15 border border-[#00F593]/30 text-[#c6ffe7] rounded-br-sm"
                        : m.role === "assistant" ? "bg-[#0A1120] border border-[#14233d] text-[#9db5d8] rounded-bl-sm"
                        : "bg-[#151515] border border-[#222] text-white rounded-bl-sm"}`}>
                      <div className="text-[9px] uppercase tracking-wide opacity-60 mb-0.5 flex items-center gap-1">
                        {m.role === "agent" ? <><Headphones className="h-2.5 w-2.5" />{m.agent_name || "Ajan"}</> : m.role === "assistant" ? <><Bot className="h-2.5 w-2.5" />AI</> : <><User className="h-2.5 w-2.5" />Kliyan</>}
                      </div>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 p-3 border-t border-[#222]">
                <Input data-testid="support-reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                  placeholder="Ekri repons ou bay kliyan an…" className="bg-[#0A0A0A] border-[#222] rounded-full h-11" />
                <Button onClick={send} disabled={sending || !reply.trim()} data-testid="support-reply-send"
                  className="h-11 w-11 shrink-0 rounded-full p-0 bg-[#00F593] text-black hover:bg-[#00d97f]">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
